/**
 * Notifications selectors — memoized via reselect.
 */

import { createSelector } from 'reselect';
import { Notification } from '@/infra/types/domain';
import { NotificationsState } from './ducks';

// ─── Input Selectors ───────────────────────────────────────

type StateWithNotifications = { notifications: NotificationsState } | NotificationsState;

function getNotificationsState(state: StateWithNotifications): NotificationsState {
  return 'notifications' in state && 'items' in (state as any).notifications
    ? (state as { notifications: NotificationsState }).notifications
    : (state as NotificationsState);
}

// ─── Derived Selectors ─────────────────────────────────────

export const selectNotifications = createSelector(
  [(state: StateWithNotifications) => getNotificationsState(state).items],
  (items): Notification[] =>
    items.filter((n) => n.status !== 'dismissed')
);

export const selectUnreadNotifications = createSelector(
  [selectNotifications],
  (notifications): Notification[] =>
    notifications.filter((n) => n.status === 'unread')
);

export const selectUnreadCount = createSelector(
  [selectUnreadNotifications],
  (unread): number => unread.length
);

export const selectNotificationsLoading = (state: StateWithNotifications): boolean => {
  return getNotificationsState(state).loading;
};

export const selectNotificationsError = (state: StateWithNotifications): string | null => {
  return getNotificationsState(state).error;
};
